import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, TextField, Typography } from '@mui/material';
import {
  BookingAddressSuggestion,
  portalApi,
} from '../services/portalApi';
import { useTranslation } from 'react-i18next';

interface BookingAddressAutocompleteProps {
  slug: string;
  value: string;
  onChange: (value: string) => void;
}

const getSuggestionLabel = (suggestion: BookingAddressSuggestion): string =>
  suggestion.fullText ||
  [suggestion.mainText, suggestion.secondaryText].filter(Boolean).join(', ');

const BookingAddressAutocomplete: React.FC<BookingAddressAutocompleteProps> = ({
  slug,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<BookingAddressSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedAddressRef = useRef('');
  const selectionRequestRef = useRef(0);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setSuggestions([]);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const query = value.trim();
    if (
      query.length < 3 ||
      !slug ||
      query === selectedAddressRef.current
    ) {
      setSuggestions([]);
      return undefined;
    }

    let active = true;
    const timeout = window.setTimeout(() => {
      portalApi
        .getBookingAddressSuggestions(slug, query)
        .then((results) => {
          if (active) {
            setSuggestions(results);
            setActiveIndex(-1);
          }
        })
        .catch(() => {
          if (active) setSuggestions([]);
        });
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [slug, value]);

  const selectSuggestion = async (suggestion: BookingAddressSuggestion) => {
    const fallbackAddress = getSuggestionLabel(suggestion);
    const requestId = selectionRequestRef.current + 1;
    selectionRequestRef.current = requestId;
    selectedAddressRef.current = fallbackAddress;
    setSuggestions([]);
    setActiveIndex(-1);
    onChange(fallbackAddress);

    try {
      const details = await portalApi.getBookingAddressDetails(
        slug,
        suggestion.placeId,
      );
      if (requestId !== selectionRequestRef.current) return;

      const resolvedAddress = details?.formattedAddress || fallbackAddress;
      selectedAddressRef.current = resolvedAddress;
      onChange(resolvedAddress);
    } catch {
      // Keep the selected prediction text so manual entry still works.
    }
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        label={t('Service address')}
        value={value}
        onChange={(event) => {
          selectionRequestRef.current += 1;
          selectedAddressRef.current = '';
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          if (!suggestions.length) return;
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => (index + 1) % suggestions.length);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) =>
              index <= 0 ? suggestions.length - 1 : index - 1,
            );
          } else if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            void selectSuggestion(suggestions[activeIndex]);
          } else if (event.key === 'Escape') {
            setSuggestions([]);
            setActiveIndex(-1);
          }
        }}
        fullWidth
        autoComplete="street-address"
        inputProps={{
          role: 'combobox',
          'aria-autocomplete': 'list',
          'aria-expanded': suggestions.length > 0,
          'aria-controls': 'booking-address-suggestions',
          'aria-activedescendant':
            activeIndex >= 0
              ? `booking-address-suggestion-${activeIndex}`
              : undefined,
        }}
      />

      {suggestions.length > 0 && (
        <Paper
          id="booking-address-suggestions"
          role="listbox"
          elevation={4}
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 20,
            overflow: 'hidden',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <Box
              id={`booking-address-suggestion-${index}`}
              key={suggestion.placeId}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => void selectSuggestion(suggestion)}
              sx={{
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                bgcolor:
                  index === activeIndex ? 'action.hover' : 'background.paper',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography fontWeight={600}>{suggestion.mainText}</Typography>
              <Typography variant="body2" color="text.secondary">
                {suggestion.secondaryText}
              </Typography>
            </Box>
          ))}
          <Typography
            variant="caption"
            sx={{ display: 'block', px: 2, py: 0.75, color: 'text.secondary' }}
          >
            Google Maps
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default BookingAddressAutocomplete;
